import {CSSProperties, useEffect, useState} from 'react';
import {
    useDataRequestFormUpdate,
    useResearcherSubtitle,
    useResearchTeamCreate,
    useResearchTeamPermissions
} from '@vivli/features/data-requests/infrastructure/hook';
import {IDataRequest, IResearchTeamMemberDetail} from '@vivli/features/data-requests/infrastructure/interface';
import { FieldHeaderComponent } from '@vivli/shared/components';
import { Color, Size } from '@vivli/shared/theme';
import { ResearchTeamMemberRoleEnum } from '@vivli/features/data-requests/infrastructure/enum';
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form';
import { TeamMemberFormComponent } from './team-member-form.component';
import { DraftTeamMemberModalComponent } from './draft-team-member-modal.component';
import { useDataRequestsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';
import { first } from 'rxjs/operators';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { DTICommonConst } from '@vivli/shared/infrastructure/constants';
import { ButtonView } from '@vivli/shared/views';
import { ResearchTeamGridComponent } from './research-team-grid.component';

const containerStyle: CSSProperties = {
    padding: Size.INSET_SIZE,
    paddingTop: 0
};

const formContainerStyle: CSSProperties = {
    marginBottom: '40px'
};

const btnStyle = {
    backgroundColor: Color.WHITE,
    borderColor: '#A0A0A0',
    borderWidth: 1,
    borderStyle: 'solid',
    color: '#A0A0A0'
};

const addContainerStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '15px'
};

export const DraftResearchTeamComponent = () => {
    const modalService = useModalApiContext();
    const createResearcher = useResearchTeamCreate();
    const useSubtitle = useResearcherSubtitle();
    const dataRequestsService = useDataRequestsApiContext();
    const { isAdminEditing, dataRequest, isSavingDataRequest } = useDataRequestContext();
    const [isChangingOwner, setIsChangingOwner] = useState(false);
    const handleDataRequestFormUpdate = useDataRequestFormUpdate();
    const { userCanEdit, userCanDelete } = useResearchTeamPermissions(dataRequest, isAdminEditing);

    const arrayName = 'researchTeam.researchTeamDetails';
    const formApi = useFormContext<IDataRequest>();
    const {fields, append, remove, insert, update} = useFieldArray({name: arrayName, control: formApi.control});

    const getFieldName = (index: number) => `${arrayName}.${index}`;

    const deleteFromFieldArray = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to remove "${teamMember.firstName} ${teamMember.lastName}"?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                removeFromGrid(teamMember);
            }
        });
    };

    const removeFromGrid = (teamMember: IResearchTeamMemberDetail) => {
        const index = fields.findIndex((tm) => tm.id === teamMember.id);
        remove(index);
    };

    const addResearcher = () => {
        const teamMember = createResearcher(ResearchTeamMemberRoleEnum.AdditionalResearcher);
        const modalId = modalService.custom(
            <DraftTeamMemberModalComponent
                teamMember={teamMember}
                onSave={(teamMember) => {
                    modalService.dismiss(modalId);
                    append(teamMember);
                }}
                onCancel={() => {
                    modalService.dismiss(modalId);
                }}
                dataRequest={dataRequest}
                userCanEdit={userCanEdit}
                userCanDelete={false}
            />,
            {
                style: {
                    width: '80%',
                    height: '80%',
                    overflowY: 'auto'
                }
            }
        );
    };

    const editResearcher = (teamMember: IResearchTeamMemberDetail, fieldIndex: number) => {
        const fieldName = getFieldName(fieldIndex);
        const modalId = modalService.custom(
            <DraftTeamMemberModalComponent
                teamMember={teamMember}
                onSave={(updatedTeamMember) => {
                    modalService.dismiss(modalId);
                    update(fieldIndex, updatedTeamMember);
                }}
                onCancel={() => {
                    modalService.dismiss(modalId);
                }}
                onDelete={(teamMember) => {
                    modalService.dismiss(modalId);
                    deleteFromFieldArray(teamMember);
                }}
                dataRequest={dataRequest}
                userCanEdit={userCanEdit}
                userCanDelete={userCanDelete && !teamMember.isAdmin}
            />,
            {
                style: {
                    width: '80%',
                    height: '80%',
                    overflowY: 'auto'
                }
            }
        );
    };

    const removeResearcher = (teamMember: IResearchTeamMemberDetail, fieldIndex: number) => {
        const confirmMsg = `Are you sure you want to remove "${teamMember.firstName} ${teamMember.lastName}"?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                remove(fieldIndex);
            }
        });
    };

    const removeStatistician = () => {
        const statisticianIndex = fields.findIndex((rtd) => rtd.isStatistician && !rtd.isLeadResearcher);
        if (statisticianIndex >= 0) {
            remove(statisticianIndex);
        }
    };

    const addStatistician = () => {
        const statistician = createResearcher(ResearchTeamMemberRoleEnum.Statistician);
        insert(1, statistician);
    };

    const setLeadIsStatistician = (leadIsStatistician: boolean) => {
        const leadMember = fields.find((rtd) => rtd.isLeadResearcher);
        const statisticianMember = fields.find((rtd) => rtd.isStatistician && !rtd.isLeadResearcher);

        if (leadIsStatistician && !leadMember.isStatistician) {
            removeStatistician();
        } else if (!statisticianMember && !leadIsStatistician) {
            addStatistician();
        }
    };

    const handleOnMakeOwner = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to make "${teamMember.firstName} ${teamMember.lastName}" the Admin of the data request?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                setIsChangingOwner(true);
                dataRequestsService
                    .changeDataRequestOwner(dataRequest.id, teamMember)
                    .pipe(first())
                    .subscribe({
                        next: (dr) => {
                            handleDataRequestFormUpdate(dr);
                            setIsChangingOwner(false);
                        },
                        error: modalService.error
                    });
            }
        });
    };

    return (
        <div style={containerStyle}>
            {fields.map((field, i) => {
                const teamMember = field as IResearchTeamMemberDetail;
                const fieldName = getFieldName(i);
                const subTitle = useSubtitle(teamMember);

                if (teamMember.isLeadResearcher || teamMember.isStatistician) {
                    return (
                        <div key={field.id}>
                            <div style={formContainerStyle}>
                                <TeamMemberFormComponent
                                    setLeadIsStatistician={setLeadIsStatistician}
                                    fieldName={fieldName}
                                    dataRequest={dataRequest}
                                    isLoading={isSavingDataRequest}
                                    subTitle={subTitle}
                                    readonly={false}
                                    teamMember={teamMember}
                                />
                            </div>

                            {teamMember.isStatistician && (
                                <div style={addContainerStyle}>
                                    <FieldHeaderComponent title={'ADDITIONAL RESEARCHERS'} />
                                    {userCanEdit && (
                                        <ButtonView style={btnStyle} onClick={addResearcher} dataId={DTICommonConst.AddButton}>
                                            Add +
                                        </ButtonView>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                }
            })}

            <ResearchTeamGridComponent members={fields} draftMode={true} onRemove={removeFromGrid} />
        </div>
    );
};
